import {
  Box,
  Center,
  Divider,
  Flex,
  type FlexProps,
  Heading,
  ListItem,
  OrderedList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  UnorderedList,
} from '@chakra-ui/react'
import { ContentfulLivePreview } from '@contentful/live-preview'
import {
  type Options,
  documentToReactComponents,
} from '@contentful/rich-text-react-renderer'
import {
  BLOCKS,
  type Document,
  INLINES,
  MARKS,
} from '@contentful/rich-text-types'
import { Prose } from '@nikolovlazar/chakra-ui-prose'
import { type Sys } from 'contentful'
import { merge } from 'lodash'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { PopoutIcon } from '~/Assets/icons'
import NavigationHyperLink from '~/components/section-modules/navigation-element/NavigationHyperLink/NavigationHyperLink'
import DynamicComponent from '~/containers/DynamicComponent/DynamicComponent'
import { getClient } from '~/hooks/useContentful'
import { IEntry, INavigationElement } from '~/types/generated/contentful'
import { ensureLinkHasProtocol } from '~/utils/ensureLinkHasProtocol'
import { pxToRem } from '~/utils/pxToRem'

import { appUrl } from '../../../utils/consts'
import ContentfulImage from '../ContentfulImage/ContentfulImage'

const renderOptions: Options = {
  renderText: text => {
    return text
      .split('\n')
      .reduce((children: any, textSegment: string, index: number) => {
        return [...children, index > 0 && <br key={index} />, textSegment]
      }, [])
  },
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: ({ data }) => {
      const [hydrated, setHydrated] = useState<{ target: IEntry }>() // Needed to prevent Hydration Mismatch error
      useEffect(() => {
        if (data) setHydrated(data as unknown as { target: IEntry })
      }, [data])

      return hydrated && <DynamicComponent componentData={hydrated.target} />
    },
    [INLINES.ASSET_HYPERLINK]: ({ data }, children) => {
      const url = data.target.fields.file.url
      const formattedUrl = ensureLinkHasProtocol(url)

      return (
        <Text
          as={Link}
          href={formattedUrl}
          target={formattedUrl?.startsWith(appUrl!) ? '_self' : '_blank'}
          rel={formattedUrl?.startsWith(appUrl!) ? '' : 'noopener noreferrer'}
          _hover={{ textDecoration: 'none' }}
        >
          {children}
        </Text>
      )
    },
    [INLINES.HYPERLINK]: ({ data }, children) => {
      if (!data.uri) {
        return
      }

      const isExternal = !data.uri?.startsWith(appUrl)

      return (
        <Text
          as={Link}
          href={data.uri}
          target={data.uri?.startsWith(appUrl) ? '_self' : '_blank'}
          rel={data.uri?.startsWith(appUrl) ? '' : 'noopener noreferrer'}
          variant="inherit"
          _hover={{ textDecoration: 'none' }}
        >
          {children}{' '}
          {isExternal && <PopoutIcon aria-label="opens in new tab" />}
        </Text>
      )
    },
    [INLINES.ENTRY_HYPERLINK]: ({ data }, children) => {
      const isPage = data.target.sys.contentType?.sys.id === 'page'
      const componentData: Partial<INavigationElement> = {
        fields: {
          text: '',
          mainText: children as unknown as string,
          contentType: 'hyperlink',
          type: isPage ? 'Internal' : 'Jump To',
          redirectReference: isPage ? data.target : undefined,
          scrollToContentReference: !isPage ? data.target : undefined,
        },
      }

      return (
        <NavigationHyperLink
          componentData={componentData as INavigationElement}
          textDecoration="underline"
          _hover={{ textDecoration: 'none' }}
        />
      )
    },
    [BLOCKS.EMBEDDED_ENTRY]: ({ data }) => {
      const contentId = data?.target?.sys?.id
      const [componentData, setComponentData] = useState<IEntry>()

      useEffect(() => {
        if (!contentId) {
          return
        }
        const setEntry = async () => {
          const entry = await getClient(true).getEntry(contentId, {
            include: 10,
          })
          setComponentData(entry as IEntry)
        }
        setEntry()
      }, [contentId])

      return componentData && <DynamicComponent componentData={componentData} />
    },
    [BLOCKS.EMBEDDED_ASSET]: node => {
      return (
        <Box marginBottom={pxToRem(26)} borderRadius="20px">
          {node.data.target && <ContentfulImage image={node.data.target} />}
        </Box>
      )
    },
    [BLOCKS.OL_LIST]: (node, children) => <OrderedList>{children}</OrderedList>,
    [BLOCKS.UL_LIST]: (node, children) => (
      <UnorderedList>{children}</UnorderedList>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => <ListItem>{children}</ListItem>,
    [BLOCKS.QUOTE]: (node, children) => (
      <Prose position="relative">
        <Box
          fontSize={{ base: pxToRem(70), md: pxToRem(100) }}
          lineHeight="1"
          position="absolute"
          top="20px"
          right={{ base: '90%', sm: '101%' }}
          color="primary.teal.60"
          aria-hidden="true"
        >
          “
        </Box>
        <blockquote>{children}</blockquote>
        <Box
          fontSize={{ base: pxToRem(70), md: pxToRem(100) }}
          lineHeight="1"
          position="absolute"
          bottom="20px"
          left={{ base: '90%', sm: '101%' }}
          color="primary.teal.60"
          transform="rotate(180deg)"
          aria-hidden="true"
        >
          “
        </Box>
      </Prose>
    ),

    [BLOCKS.TABLE]: (node, children) => (
      <TableContainer tabIndex={0}>
        <Table variant="wysiwyg" colorScheme="teal">
          <Tbody>{children}</Tbody>
        </Table>
      </TableContainer>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (node, children) => <Th>{children}</Th>,
    [BLOCKS.TABLE_ROW]: (node, children) => <Tr>{children}</Tr>,
    [BLOCKS.TABLE_CELL]: (node, children) => <Td>{children}</Td>,
    [BLOCKS.HR]: () => (
      <Center h="20px">
        <Divider orientation="horizontal" />
      </Center>
    ),
    [BLOCKS.HEADING_1]: (node, children) => (
      <Heading as="h1" variant="wysiwyg_h1" data-tag="heading">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Heading as="h2" variant="wysiwyg_h2" data-tag="heading">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Heading as="h3" variant="wysiwyg_h3" data-tag="heading">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Heading as="h4" variant="wysiwyg_h4" data-tag="heading">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <Heading as="h5" variant="wysiwyg_h5" data-tag="heading">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <Heading as="h6" variant="wysiwyg_h6" data-tag="heading">
        {children}
      </Heading>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Text variant="wysiwyg_p" data-tag="paragraph">
        {children}
      </Text>
    ),
  },
  renderMark: {
    [MARKS.BOLD]: text => {
      return (
        <Box
          as="span"
          sx={{
            '[data-tag="heading"] &': {
              fontWeight: 350,
            },
            '[data-tag="paragraph"] &': {
              fontWeight: 400,
            },
          }}
        >
          {text}
        </Box>
      )
    },
  },
}

export type EntryId = Sys['id']

export default function DocumentToReactRender({
  componentData,
  size = 'md',
  entryId,
  fieldId,
  options = {},
  ...props
}: {
  componentData?: Document
  size?: 'sm' | 'md' | 'lg' | 'xl'
  entryId?: EntryId
  fieldId?: string
  options?: Options
} & FlexProps) {
  const fontSizes: {
    [K in typeof size]: string
  } = {
    sm: pxToRem(12.5),
    md: pxToRem(16),
    lg: pxToRem(17.8),
    xl: pxToRem(19.6),
  }

  const mergedRenderOptions = merge({}, renderOptions, options)

  const livePreviewProps =
    fieldId && entryId
      ? ContentfulLivePreview.getProps({
          entryId: entryId,
          fieldId: fieldId,
        })
      : null

  return componentData ? (
    <Flex
      className="document-to-react-render"
      direction="column"
      fontSize={fontSizes[size] ?? '1rem'}
      color="inherit"
      {...props}
      {...livePreviewProps}
    >
      {documentToReactComponents(componentData, mergedRenderOptions)}
    </Flex>
  ) : (
    <></>
  )
}
